// =============================================================================
// File: frontend/src/components/TwinSimulations.jsx
// Digital Twin Simulations Tab — What-If predictions
// Patent claims exercised: P-011 C2, C8, C9, C16
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import SimulationModal from './SimulationModal';
import XaiPromptPanel from './XaiPromptPanel';
import { useCreditSpend } from '../contexts/CreditSpendContext';
import { useConfirm } from './ConfirmProvider';

const SIMULATION_CATEGORIES = [
  { key: 'diet_change', label: 'Diet', icon: '🥗', description: 'What if I change my diet?' },
  { key: 'medication_change', label: 'Medication', icon: '💊', description: 'What if I adjust medication?' },
  { key: 'exercise_change', label: 'Exercise', icon: '🏃', description: 'What if I change my routine?' },
  { key: 'supplement_change', label: 'Supplement', icon: '💎', description: 'What if I add a supplement?' },
  { key: 'schedule_change', label: 'Schedule', icon: '⏰', description: 'What if I change timing?' },
];

// ---------------------------------------------------------------------------
// Pantry feasibility rendering.
//
// The producer is backend/app/services/twin_simulation_service.py
// `_check_pantry_feasibility`, which returns ONE OF TWO shapes and never a
// pre-rendered sentence:
//
//   empty pantry : { feasible:false, reason, suggestions:[] }
//   normal       : { feasible, pantry_items, items_with_macro_data, food_categories,
//                    macro_totals, macro_coverage_days, macro_shortfalls,
//                    macro_sufficient, diet_type }
//
// Anything not recognised degrades to a neutral line — never to JSON.stringify.
// ---------------------------------------------------------------------------
const MACRO_LABELS = { protein_g: 'Protein', carbs_g: 'Carbs', fat_g: 'Fat' };

const titleCase = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function PantryFeasibility({ data }) {
  if (!data || typeof data !== 'object') return null;

  const dietType = data.diet_type ? ` to a ${titleCase(data.diet_type)} pattern` : '';
  const itemCount = Number.isFinite(data.pantry_items) ? data.pantry_items : null;
  const withMacros = Number.isFinite(data.items_with_macro_data) ? data.items_with_macro_data : null;
  const categories = Array.isArray(data.food_categories) ? data.food_categories.filter(Boolean) : [];
  const shortfalls = Array.isArray(data.macro_shortfalls) ? data.macro_shortfalls.filter(Boolean) : [];
  const suggestions = Array.isArray(data.suggestions) ? data.suggestions.filter(Boolean) : [];
  const coverage = data.macro_coverage_days && typeof data.macro_coverage_days === 'object'
    ? Object.entries(data.macro_coverage_days)
    : [];

  const feasible = data.feasible === true;
  const hasDetail = itemCount != null || data.reason || categories.length > 0 ||
    coverage.length > 0 || shortfalls.length > 0 || suggestions.length > 0;

  return (
    <div className="dt-pantryCheck">
      <h5>Pantry Feasibility</h5>
      <p className={feasible ? 'dt-pantryCheck-verdict is-ok' : 'dt-pantryCheck-verdict is-warn'}>
        {feasible
          ? `Your pantry can support this change${dietType}.`
          : `Your pantry is not currently stocked for this change${dietType}.`}
      </p>

      {data.reason && <p className="dt-pantryCheck-reason">{data.reason}</p>}

      {itemCount != null && (
        <p className="dt-pantryCheck-counts">
          {itemCount} item{itemCount === 1 ? '' : 's'} in your pantry
          {withMacros != null && (
            withMacros > 0
              ? `, ${withMacros} with nutrition data.`
              : `, none of which carry nutrition data — macro coverage could not be assessed.`
          )}
        </p>
      )}

      {categories.length > 0 && (
        <p className="dt-pantryCheck-cats">
          Categories on hand: {categories.map(titleCase).join(', ')}.
        </p>
      )}

      {withMacros > 0 && coverage.length > 0 && (
        <div className="dt-pantryCheck-macros">
          <span className="dt-pantryCheck-macros-label">Estimated days of cover</span>
          <div className="dt-pantryCheck-macroGrid">
            {coverage.map(([key, days]) => (
              <div key={key} className="dt-pantryCheck-macro">
                <span className="dt-pantryCheck-macro-name">{MACRO_LABELS[key] || titleCase(key)}</span>
                <span className="dt-pantryCheck-macro-days">
                  {Number.isFinite(days) ? `${days} day${days === 1 ? '' : 's'}` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {shortfalls.length > 0 && (
        <p className="dt-pantryCheck-short">
          Short on: {shortfalls.map(titleCase).join(', ')}.
        </p>
      )}

      {suggestions.length > 0 && (
        <ul className="dt-pantryCheck-suggestions">
          {suggestions.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}

      {!hasDetail && (
        <p className="dt-pantryCheck-reason">
          A detailed pantry breakdown is not available for this simulation.
        </p>
      )}
    </div>
  );
}

export default function TwinSimulations({ fetchWithAuth, twinState }) {
  const confirm = useConfirm();
  const { spend } = useCreditSpend();  // #77 twin_simulation = 2 credits (chip on the modal Run button, low-balance confirm)
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [runningSimulation, setRunningSimulation] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadSimulations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/simulations?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setSimulations(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load simulations:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadSimulations(); }, [loadSimulations]);

  const handleRunSimulation = async (simulationType, inputParams, horizonDays) => {
    setRunningSimulation(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          simulation_type: simulationType,
          input_params: inputParams,
          horizon_days: horizonDays,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        setDetailModal(result);
        setShowModal(false);
        loadSimulations();
      } else {
        const err = await res.json();
        alert(Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join(', ') : err.detail || 'Simulation failed');
      }
    } catch (err) {
      alert('Network error running simulation');
    } finally {
      setRunningSimulation(false);
    }
  };

  const handleDelete = async (simId) => {
    if (!(await confirm({
      title: 'Delete this simulation?',
      message: 'The simulation and its results will be permanently removed. This cannot be undone.',
      confirmLabel: 'Delete Simulation',
      destructive: true,
    }))) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/simulations/${simId}`, { method: 'DELETE' });
      if (res.ok) {
        loadSimulations();
        if (detailModal?.id === simId) setDetailModal(null);
      }
    } catch (err) {
      console.error('Failed to delete simulation:', err);
    }
  };

  const handleTrack = async (simId) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/simulations/${simId}/track`, { method: 'POST' });
      if (res.ok) {
        loadSimulations();
      }
    } catch (err) {
      console.error('Failed to track simulation:', err);
    }
  };

  const handleViewDetail = async (simId) => {
    try {
      setLoadingDetail(true);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/simulations/${simId}`);
      if (res.ok) {
        const data = await res.json();
        setDetailModal(data);
      }
    } catch (err) {
      console.error('Failed to load simulation detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Format tracking dates for display
  const formatTrackingInfo = (sim) => {
    if (!sim.tracking_start || !sim.tracking_end) return null;
    const start = new Date(sim.tracking_start);
    const end = new Date(sim.tracking_end);
    const today = new Date();
    const daysLeft = Math.max(0, Math.ceil((end - today) / (1000 * 60 * 60 * 24)));
    const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    const elapsed = totalDays - daysLeft;
    const pct = Math.min(100, Math.round((elapsed / totalDays) * 100));
    return { start, end, daysLeft, totalDays, elapsed, pct };
  };

  return (
    <div className="dt-simulations">
      {/* AI Operation Spinner Overlay */}
      {(runningSimulation && !showModal) && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Running simulation — analyzing your health data...</p>
          </div>
        </div>
      )}
      {loadingDetail && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Loading simulation details...</p>
          </div>
        </div>
      )}

      {/* New Simulation Card */}
      <div className="dt-newSimCard">
        <h3>What would happen if...</h3>
        <div className="dt-categoryGrid">
          {SIMULATION_CATEGORIES.map(cat => (
            <button
              key={cat.key}
              className="dt-categoryBtn"
              onClick={() => { setSelectedCategory(cat.key); setShowModal(true); }}
            >
              <span className="dt-categoryBtn-icon">{cat.icon}</span>
              <span className="dt-categoryBtn-label">{cat.label}</span>
              <span className="dt-categoryBtn-desc">{cat.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Past Simulations List */}
      <div className="dt-pastSims">
        <h3>Past Simulations</h3>
        {loading ? (
          <div className="dt-loading"><div className="dt-spinner"></div></div>
        ) : simulations.length === 0 ? (
          <p className="dt-empty-text">No simulations yet. Try a "What if..." scenario above.</p>
        ) : (
          <div className="dt-simsList">
            {simulations.map(sim => (
              <div key={sim.id} className="dt-simItem">
                <div className="dt-simItem-header">
                  <span className="dt-simItem-type">
                    {SIMULATION_CATEGORIES.find(c => c.key === sim.simulation_type)?.icon || '🔮'}{' '}
                    {sim.simulation_type.replace(/_/g, ' ')}
                  </span>
                  <div className="dt-simItem-badgeGroup">
                    <button
                      className={`dt-simItem-statusBtn dt-status-${sim.status}`}
                      onClick={() => handleViewDetail(sim.id)}
                    >
                      {sim.status === 'tracking' ? 'TRACKING' : sim.status === 'evaluated' ? 'EVALUATED' : 'COMPLETED'}
                    </button>
                    {/* M-18 (Dev audit 2026-10-20): this said "Click for Details" but was plain text; only the
                        status badge opened the detail. It is now a real button for the same action. */}
                    <button
                      type="button"
                      className="dt-simItem-hint"
                      data-testid="sim-details"
                      onClick={() => handleViewDetail(sim.id)}
                    >
                      Click for Details
                    </button>
                  </div>
                </div>
                <div className="dt-simItem-meta">
                  <span>{new Date(sim.created_at).toLocaleDateString()}</span>
                  <span>{sim.horizon_days}d horizon</span>
                  {sim.accuracy_score != null && (
                    <span className="dt-simItem-accuracy">
                      Accuracy: {(sim.accuracy_score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="dt-simItem-actions">
                  {sim.status === 'completed' && (
                    <button
                      className="btn dt-trackBtn"
                      onClick={() => handleTrack(sim.id)}
                      title="Start tracking your actual health data against this simulation's predictions to measure AI accuracy"
                    >
                      Track This
                    </button>
                  )}
                  <button
                    className="btn dt-deleteBtn"
                    onClick={() => handleDelete(sim.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Simulation Input Modal */}
      {showModal && (
        <SimulationModal
          category={selectedCategory}
          onRun={(...args) => spend('twin_simulation', 'Run this simulation?', () => handleRunSimulation(...args))}
          onClose={() => setShowModal(false)}
          running={runningSimulation}
        />
      )}

      {/* Simulation Detail Modal */}
      {detailModal && (
        <div className="modal-overlay" onClick={() => setDetailModal(null)}>
          <div className="modal-content dt-simDetailModal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {SIMULATION_CATEGORIES.find(c => c.key === detailModal.simulation_type)?.icon || '🔮'}{' '}
                {detailModal.simulation_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} — {
                  detailModal.status === 'tracking' ? 'Tracking in Progress' :
                  detailModal.status === 'evaluated' ? 'Tracking Complete' :
                  'Simulation Results'
                }
              </h2>
              <button className="modal-close" onClick={() => setDetailModal(null)}>✕</button>
            </div>
            <div className="modal-body">

              {/* ── TRACKING STATUS ── */}
              {detailModal.status === 'tracking' && (() => {
                const info = formatTrackingInfo(detailModal);
                return info ? (
                  <div className="dt-trackingStatus">
                    <h4>Predicted vs. Actual Tracking</h4>
                    <p>Your Digital Twin is comparing the AI's predictions against your actual health data
                    over the {detailModal.horizon_days}-day horizon period.</p>

                    <div className="dt-trackingProgress">
                      <div className="dt-trackingProgress-bar">
                        <div className="dt-trackingProgress-fill" style={{ width: `${info.pct}%` }}></div>
                      </div>
                      <div className="dt-trackingProgress-labels">
                        <span>Started: {info.start.toLocaleDateString()}</span>
                        <span>{info.daysLeft} day{info.daysLeft !== 1 ? 's' : ''} remaining</span>
                        <span>Ends: {info.end.toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="dt-trackingExplainer">
                      <h4>What happens when tracking ends?</h4>
                      <p>When the tracking window closes, the system computes an <strong>accuracy score</strong> by
                      comparing your actual health metrics against what the AI predicted. This score feeds back
                      into the AI as a few-shot example, making future simulations more accurate for you specifically.</p>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* ── EVALUATED RESULT ──
                  Was dead until 2026-10-04: this is gated on status === 'evaluated' but the backend
                  wrote 'completed' on evaluation, which is also the status a simulation is CREATED
                  with. Fixed at the source — twin_simulation_service.evaluate_prediction now writes
                  'evaluated' as the terminal state. Guarded by
                  tests/backend/test_twin_simulation_render_contract.py. */}
              {detailModal.status === 'evaluated' && (
                <div className="dt-evaluatedResult">
                  <h4>Tracking Complete — Prediction Accuracy</h4>
                  {detailModal.accuracy_score != null ? (
                    <div className="dt-accuracyDisplay">
                      <span className="dt-accuracyDisplay-score">
                        {(detailModal.accuracy_score * 100).toFixed(0)}%
                      </span>
                      <span className="dt-accuracyDisplay-label">Prediction Accuracy</span>
                    </div>
                  ) : (
                    <p>Accuracy score pending — insufficient data for comparison.</p>
                  )}
                  {/* 2026-10-04: was a <pre>{JSON.stringify(...)}</pre> dump — the same defect as
                      the pantry block below, in the same modal. Currently unreachable (see the
                      status note on the enclosing block) but fixed so it cannot leak if the status
                      mismatch is ever corrected. Shape is
                      { metric_name: { actual_avg, predicted_avg, data_points } }
                      from twin_simulation_service.evaluate_prediction. */}
                  {detailModal.actual_outcomes && Object.keys(detailModal.actual_outcomes).length > 0 && (
                    <div className="dt-actualOutcomes">
                      <h4>Actual Outcomes</h4>
                      <div className="dt-trajGrid">
                        {Object.entries(detailModal.actual_outcomes).map(([metric, o]) => (
                          <div key={metric} className="dt-trajCard">
                            <span className="dt-trajCard-metric">{metric.replace(/_/g, ' ')}</span>
                            <span className="dt-trajCard-value">
                              {Number.isFinite(o?.actual_avg) ? o.actual_avg.toFixed(1) : '—'}
                            </span>
                            <span className="dt-trajCard-confidence">
                              Predicted: {Number.isFinite(o?.predicted_avg) ? o.predicted_avg.toFixed(1) : '—'}
                            </span>
                            <span className="dt-trajCard-confidence">
                              {o?.data_points ?? 0} data point{o?.data_points === 1 ? '' : 's'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── PREDICTIONS (shown for all statuses) ── */}
              <div className="dt-modalPredictions">
                <h4>{detailModal.status === 'tracking' ? 'Original Predictions' : 'AI Predictions'}</h4>
                {/* Framing fix (Vincent 2026-10-19): a simulation is the AI's prediction from THIS user's own
                    data, not cited medical guidance, and must say so before anything else. */}
                <p className="dt-predictionFrame" data-testid="ai-prediction-frame">
                  AI prediction from your own data — not cited medical guidance. Discuss any change with your clinician.
                </p>

                {/* Input Parameters */}
                {detailModal.input_params && (
                  <div className="dt-inputParams">
                    <h5>Simulation Input</h5>
                    <div className="dt-inputParams-grid">
                      {Object.entries(detailModal.input_params).map(([key, val]) => (
                        val ? (
                          <div key={key} className="dt-inputParam">
                            <span className="dt-inputParam-key">{key.replace(/_/g, ' ')}</span>
                            <span className="dt-inputParam-val">{String(val)}</span>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}

                {/* Domain Impacts */}
                {detailModal.predictions?.domain_impacts && (
                  <div className="dt-domainImpacts">
                    <h5>Impact by Domain</h5>
                    <div className="dt-impactGrid">
                      {detailModal.predictions.domain_impacts.map((impact, i) => (
                        <div key={i} className={`dt-impactCard dt-impact-${impact.magnitude}`}>
                          <span className="dt-impactCard-domain">{impact.domain}</span>
                          <span className="dt-impactCard-impact">{impact.impact}</span>
                          <span className="dt-impactCard-magnitude">{impact.magnitude}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metric Trajectories */}
                {detailModal.predictions?.metric_trajectories && (
                  <div className="dt-trajectories">
                    <h5>Predicted Metric Changes</h5>
                    <div className="dt-trajGrid">
                      {Object.entries(detailModal.predictions.metric_trajectories).map(([metric, data]) => (
                        <div key={metric} className="dt-trajCard">
                          <span className="dt-trajCard-metric">{metric.replace(/_/g, ' ')}</span>
                          <span className="dt-trajCard-value">
                            {data.predicted_mean?.toFixed(1) || '—'}
                          </span>
                          <span className={`dt-trajCard-trend dt-trend-${data.trend || 'stable'}`}>
                            {data.trend === 'up' ? '↑' : data.trend === 'down' ? '↓' : '→'} {data.trend || 'stable'}
                          </span>
                          {data.confidence_low != null && data.confidence_high != null && (
                            <span className="dt-trajCard-confidence">
                              CI: {data.confidence_low.toFixed(1)} – {data.confidence_high.toFixed(1)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Healist Score Impact — hide if no actual data */}
                {detailModal.predictions?.healist_score_impact &&
                 (detailModal.predictions.healist_score_impact.current != null ||
                  detailModal.predictions.healist_score_impact.predicted != null) && (
                  <div className="dt-scoreImpact">
                    <h5>Healist Score Impact</h5>
                    <div className="dt-scoreImpact-values">
                      <span>Current: {detailModal.predictions.healist_score_impact.current?.toFixed(0) || '—'}</span>
                      <span className="dt-scoreImpact-arrow">→</span>
                      <span>Predicted: {detailModal.predictions.healist_score_impact.predicted?.toFixed(0) || '—'}</span>
                      <span className={`dt-scoreImpact-change ${
                        (detailModal.predictions.healist_score_impact.change || 0) >= 0 ? 'positive' : 'negative'
                      }`}>
                        ({detailModal.predictions.healist_score_impact.change >= 0 ? '+' : ''}
                        {detailModal.predictions.healist_score_impact.change?.toFixed(1) || '0'})
                      </span>
                    </div>
                  </div>
                )}

                {/* AI Explanation */}
                {detailModal.ai_explanation && detailModal.ai_explanation !== 'Unable to generate predictions at this time.' ? (
                  <div className="dt-aiExplanation">
                    <h5>AI Analysis</h5>
                    <p>{detailModal.ai_explanation}</p>
                  </div>
                ) : detailModal.status === 'tracking' && (
                  <div className="dt-aiExplanation">
                    <h5>AI Analysis</h5>
                    <p>Prediction analysis will be available when the {detailModal.horizon_days}-day tracking window
                    closes. The system will compare your actual health data against the AI's predictions and compute
                    an accuracy score. Check back after {detailModal.tracking_end
                      ? new Date(detailModal.tracking_end).toLocaleDateString()
                      : `${detailModal.horizon_days} days`}.</p>
                  </div>
                )}

                {/* Risks */}
                {detailModal.predictions?.risks?.length > 0 && !detailModal.predictions.risks.some(r => r.includes('temporarily unavailable')) && (
                  <div className="dt-risks">
                    <h5>Potential Risks</h5>
                    <ul>
                      {detailModal.predictions.risks.map((risk, i) => (
                        <li key={i}>{risk}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Pantry Feasibility — 2026-10-04: was
                    `{pf.summary || JSON.stringify(pf)}`. The backend
                    (twin_simulation_service._check_pantry_feasibility) has NEVER emitted a
                    `summary` key in either of its two shapes, so the fallback was the only branch
                    that could ever run and every user saw a raw JSON object dumped into the modal.
                    Caught in an App Review screenshot. Rendered from the real fields now. */}
                {detailModal.pantry_feasibility && (
                  <PantryFeasibility data={detailModal.pantry_feasibility} />
                )}
              </div>

              {/* XAI Panel — 2026-10-01: was a SECOND, cruder implementation that dumped the raw
                  prompt JSON into a <pre> block. It carried no citations, no tabs, and exposed the
                  system prompt verbatim. Replaced with the shared XaiPromptPanel so every AI
                  surface behaves identically and this one gains the Citations tab. */}
              {detailModal.prompt_data && (
                <XaiPromptPanel
                  promptData={detailModal.prompt_data}
                  featureName="Digital Twin Simulation"
                />
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
